import React from 'react';
import PropTypes from 'prop-types';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import PermissionDenied from 'src/components/PermissionDenied';
import { hasPermission } from 'src/utils/permissions';

/**
 * Permission Guard Component
 * 
 * This component checks if the current user has the required permissions
 * to access a specific resource or perform an action.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if permission is granted
 * @param {string[]} props.permissions - Array of required permissions
 * @param {React.ReactNode} [props.fallbackComponent] - Custom component to render when permission is denied
 * @param {string} [props.message] - Custom message for permission denied
 * 
 * @returns {React.ReactNode} Children if permission granted, fallback component if denied
 */
export const PermissionGuard = ({ 
  children, 
  permissions, 
  fallbackComponent, 
  message 
}) => {
  const user = useMockedUser();

  // Check if user has any of the required permissions
  const userHasPermission = hasPermission(
    user?.permissions,
    permissions,
    user?.isSuper
  );

  if (!userHasPermission) {
    return fallbackComponent || <PermissionDenied message={message} />;
  }

  return <>{children}</>;
};

PermissionGuard.propTypes = {
  children: PropTypes.node.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  fallbackComponent: PropTypes.node,
  message: PropTypes.string
};